import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0;
`;

const ExportBtn = styled.a`
  padding: 10px 20px;
  background: #2DBCAF;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  &:hover { background: #25a99d; }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div<{ $color: string }>`
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  border-left: 4px solid ${p => p.$color};
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1a1f2e;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
`;

const Filters = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #475569;
  background: #fff;
  cursor: pointer;
  &:focus { outline: none; border-color: #2DBCAF; }
`;

const FilterInput = styled.input`
  padding: 8px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #475569;
  &:focus { outline: none; border-color: #2DBCAF; }
`;

const Table = styled.div`
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr 100px 100px 100px 100px 140px;
  gap: 12px;
  padding: 14px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;

  @media (max-width: 1000px) {
    display: none;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr 100px 100px 100px 100px 140px;
  gap: 12px;
  padding: 14px 20px;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  &:last-child { border-bottom: none; }
  &:hover { background: #f8fafc; }

  @media (max-width: 1000px) {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
`;

const OrderId = styled.div`
  font-family: monospace;
  font-size: 12px;
  color: #475569;
`;

const UserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const UserAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1a1f2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.div`
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${p => {
    switch (p.$status) {
      case 'paid':
      case 'confirmed': return 'rgba(34, 197, 94, 0.1)';
      case 'pending': return 'rgba(245, 158, 11, 0.1)';
      case 'expired': return 'rgba(100, 116, 139, 0.1)';
      case 'failed':
      case 'cancelled': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(100, 116, 139, 0.1)';
    }
  }};
  color: ${p => {
    switch (p.$status) {
      case 'paid':
      case 'confirmed': return '#16a34a';
      case 'pending': return '#d97706';
      case 'expired': return '#64748b';
      case 'failed':
      case 'cancelled': return '#dc2626';
      default: return '#64748b';
    }
  }};
`;

const MethodBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
  text-transform: uppercase;
`;

const Amount = styled.div`
  font-weight: 600;
  color: #1a1f2e;
`;

const DateTime = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const PageInfo = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const PageBtns = styled.div`
  display: flex;
  gap: 8px;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${p => p.$active ? '#2DBCAF' : '#e2e8f0'};
  border-radius: 6px;
  font-size: 13px;
  background: ${p => p.$active ? '#2DBCAF' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#64748b'};
  cursor: pointer;
  &:hover:not(:disabled) { border-color: #2DBCAF; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

interface Payment {
  _id: string;
  orderId: string;
  userId: {
    _id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  packageType: string;
  baseAmount: number;
  discount: number;
  totalAmount: number;
  method: string;
  status: string;
  createdAt: string;
  paidAt?: string;
}

interface Summary {
  total: number;
  paid: number;
  pending: number;
  expired: number;
  failed: number;
  revenue: number;
}

interface Filters {
  status: string;
  method: string;
  startDate: string;
  endDate: string;
}

const PaymentsPage: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [summary, setSummary] = useState<Summary>({ total: 0, paid: 0, pending: 0, expired: 0, failed: 0, revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<Filters>({
    status: '',
    method: '',
    startDate: '',
    endDate: '',
  });

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');
      if (filters.status) params.set('status', filters.status);
      if (filters.method) params.set('method', filters.method);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);

      const res = await fetch(`/api/admin/payments?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPayments(data.payments);
        setSummary(data.summary);
        setTotalPages(data.pagination.pages);
        setTotal(data.pagination.total);
      }
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters(f => ({ ...f, [key]: value }));
    setPage(1);
  };

  const formatCurrency = (amount: number) => {
    return `Rp ${amount.toLocaleString('id-ID')}`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getExportUrl = () => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.method) params.set('method', filters.method);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    return `/api/admin/payments/export?${params}`;
  };

  const successRate = summary.total > 0 ? Math.round((summary.paid / summary.total) * 100) : 0;

  return (
    <>
      <PageHeader>
        <Title>💳 Pembayaran</Title>
        <ExportBtn href={getExportUrl()} download>
          📥 Export CSV
        </ExportBtn>
      </PageHeader>

      <StatsGrid>
        <StatCard $color="#3b82f6">
          <StatValue>{summary.total}</StatValue>
          <StatLabel>Total Transaksi</StatLabel>
        </StatCard>
        <StatCard $color="#22c55e">
          <StatValue>{summary.paid}</StatValue>
          <StatLabel>Berhasil ({successRate}%)</StatLabel>
        </StatCard>
        <StatCard $color="#f59e0b">
          <StatValue>{summary.pending}</StatValue>
          <StatLabel>Pending</StatLabel>
        </StatCard>
        <StatCard $color="#64748b">
          <StatValue>{summary.expired}</StatValue>
          <StatLabel>Expired</StatLabel>
        </StatCard>
        <StatCard $color="#ef4444">
          <StatValue>{summary.failed}</StatValue>
          <StatLabel>Gagal</StatLabel>
        </StatCard>
        <StatCard $color="#2DBCAF">
          <StatValue>{formatCurrency(summary.revenue)}</StatValue>
          <StatLabel>Total Revenue</StatLabel>
        </StatCard>
      </StatsGrid>

      <Filters>
        <FilterSelect
          value={filters.status}
          onChange={e => handleFilterChange('status', e.target.value)}
        >
          <option value="">Semua Status</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="confirmed">Confirmed</option>
          <option value="expired">Expired</option>
          <option value="failed">Failed</option>
        </FilterSelect>
        <FilterSelect
          value={filters.method}
          onChange={e => handleFilterChange('method', e.target.value)}
        >
          <option value="">Semua Method</option>
          <option value="qris">QRIS</option>
          <option value="bank_transfer">Bank Transfer</option>
          <option value="admin">Admin</option>
        </FilterSelect>
        <FilterInput
          type="date"
          value={filters.startDate}
          onChange={e => handleFilterChange('startDate', e.target.value)}
          placeholder="Dari tanggal"
        />
        <FilterInput
          type="date"
          value={filters.endDate}
          onChange={e => handleFilterChange('endDate', e.target.value)}
          placeholder="Sampai tanggal"
        />
      </Filters>

      <Table>
        <TableHeader>
          <div>Order ID</div>
          <div>User</div>
          <div>Paket</div>
          <div>Total</div>
          <div>Method</div>
          <div>Status</div>
          <div>Tanggal</div>
        </TableHeader>

        {loading ? (
          <EmptyState>Loading...</EmptyState>
        ) : payments.length === 0 ? (
          <EmptyState>Tidak ada data pembayaran</EmptyState>
        ) : (
          payments.map(p => (
            <TableRow key={p._id}>
              <OrderId>{p.orderId}</OrderId>
              <UserCell>
                <UserAvatar
                  src={p.userId?.avatar || '/assets/images/default-avatar.png'}
                  alt={p.userId?.name || 'User'}
                />
                <UserInfo>
                  <UserName>{p.userId?.name || 'Unknown'}</UserName>
                  <UserEmail>{p.userId?.email || '-'}</UserEmail>
                </UserInfo>
              </UserCell>
              <div style={{ textTransform: 'capitalize', fontSize: '13px' }}>
                {p.packageType}
              </div>
              <Amount>{formatCurrency(p.totalAmount)}</Amount>
              <div>
                <MethodBadge>{p.method}</MethodBadge>
              </div>
              <div>
                <StatusBadge $status={p.status}>{p.status}</StatusBadge>
              </div>
              <DateTime>{formatDate(p.createdAt)}</DateTime>
            </TableRow>
          ))
        )}

        {totalPages > 1 && (
          <Pagination>
            <PageInfo>
              Menampilkan {payments.length} dari {total} transaksi
            </PageInfo>
            <PageBtns>
              <PageBtn
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                ← Prev
              </PageBtn>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <PageBtn
                    key={pageNum}
                    $active={page === pageNum}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </PageBtn>
                );
              })}
              <PageBtn
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next →
              </PageBtn>
            </PageBtns>
          </Pagination>
        )}
      </Table>
    </>
  );
};

export default PaymentsPage;
